<template>
  <div>
    <v-file-input
      label="select files for uploading"
      :disabled="uploading || disabled"
      :loading="uploading"
      v-model="filesToUpload"
      multiple
      clearable
      small-chips
      counter
      show-size
      :rules="rules"
      :outlined="true"
      :dense="gatComponentsStore.input_dense">
      <template slot="append-outer">
        <gat-button
          caption="upload"
          icon="mdi-upload"
          :hideCaptionOnPhone="true"
          @click="uploadFiles"
          v-show="!(filesToUpload.length == 0 || !rulesAreValid || uploading)"
          :disabled="disabled" />
        <!-- <v-btn @click="uploadFiles" v-show="!(filesToUpload.length == 0 || !rulesAreValid ||uploading)" :disabled="disabled" small class="ml-2" color="primary"><v-icon class="mr-2">mdi-upload</v-icon>upload</v-btn> -->
      </template>
    </v-file-input>
    <gat-dialog
      v-model="showReplaceDocumentDialog"
      :okButtonCaption="filesToUpload.length > 1 ? 'replace files' : 'replace file'"
      :okButtonIsProminent="true"
      @okClick="replaceDialogAnswer = 'yes'"
      @cancelClick="replaceDialogAnswer = 'cancel'"
      :title="filesToUpload.length > 1 ? 'Add or replace files' : 'Add or replace file'">
      <template v-slot:delete-btn>
        <v-btn text color="primary" @click="replaceDialogAnswer = 'no'">
          <span v-if="filesToUpload.length > 1">Keep old files</span>
          <span v-else>Keep both</span>
        </v-btn>
      </template>
      <div class="pb-2" v-if="filesToUpload.length > 1">
        Files with matching names were found, do you want to replace them?
      </div>
      <div class="pb-2" v-if="filesToUpload.length == 1">
        A file with a matching name was found, do you want to replace the existing file or keep both files?
      </div>
      <v-list>
        <template v-for="file in replaceDialogContent">
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title>
                {{ file.fileName }}
              </v-list-item-title>
              <v-list-item-subtitle v-if="file.duplicates > 0">
                There are {{ file.duplicates }} files with this file name.
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-tooltip bottom left open-on-hover v-if="file.duplicates > 0">
              <template v-slot:activator="{ on, attrs }">
                <v-list-item-action v-on="on" v-bind="attrs">
                  <v-icon color="orange">mdi-alert</v-icon>
                </v-list-item-action>
              </template>
              <div>
                <div>Warning: The most recently updated file will be replaced.</div>
                <div class="pt-2">
                  Please consider removing files with the same file name to avoid this warning in the future.
                </div>
              </div>
            </v-tooltip>
          </v-list-item>
        </template>
      </v-list>
      <div class="pt-4"></div>
    </gat-dialog>
  </div>
</template>
<script setup lang="ts">
import { apiPost } from '@/store/api';
import { getDeviceIdFromCookie } from '@/store/cookieStorage';
import { computed, ref, watch } from 'vue';
import { useGatComponentsStore } from '@/store/gatComponentsStore';
import { useDocumentUploaderReplaceFilesDialog } from './composables/useDocumentUploaderReplaceFilesDialog';

const props = defineProps<{
  parentId: string | number;
  location: string;
  disabled?: boolean;
  autoUpload?: boolean;
  documents: any[];
  droppedFiles: any[];
}>();

const emit = defineEmits<{
  (e: 'file-selected', payload: any[]): void;
  (e: 'get-documents', parentId: string | number): void;
}>();

const gatComponentsStore = useGatComponentsStore();

const filesToUpload = ref<any[]>(props.droppedFiles || []);
const autoUpload = ref<boolean>(props.autoUpload || false);

const {
  showReplaceDocumentDialog,
  replaceDialogAnswer,
  checkForReplacableFiles,
  replaceDialogContent,
  getFirstReplaceFileId,
} = useDocumentUploaderReplaceFilesDialog(props, filesToUpload);

watch(
  filesToUpload.value,
  (selectedFiles) => {
    const files: any[] = [];
    if (selectedFiles) {
      selectedFiles.forEach((file) => {
        if (file && file.name) {
          files.push({ name: file.name });
        }
      });
    }
    emit('file-selected', files);
  },
  {
    immediate: true,
  }
);

const uploading = ref(false);

const rules = computed(() => [
  (files: any) => {
    for (let index = 0; index < files.length; index++) {
      const file = files[index];
      if (file.size > 10000000) {
        return 'File size must be less than 10 MB!';
      }
    }

    return true;
  },
  (files: any) => {
    if (files.length > 9) {
      return 'Max 10 files can be uploaded at once';
    }

    return true;
  },
]);

const rulesAreValid = computed(() => {
  const result = true;
  for (let index = 0; index < rules.value.length; index++) {
    const rule = rules.value[index];
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const result = rule(filesToUpload.value);
    if (result !== true) {
      return false;
    }
  }
  return result;
});

function getDocuments(parentId: string | number) {
  emit('get-documents', parentId);
}

async function uploadFiles() {
  const answer = await checkForReplacableFiles();
  if (answer === 'cancel') {
    filesToUpload.value = [];
    return;
  }
  try {
    uploading.value = true;
    const formData = new FormData();
    formData.append('location', props.location);
    formData.append('parentId', props.parentId.toString());
    for (let index = 0; index < filesToUpload.value.length; index++) {
      const file = filesToUpload.value[index];

      // https://developer.mozilla.org/en-US/docs/Web/API/File/lastModified
      let modifiedDate: string | Date = new Date(file.lastModified);
      if (!modifiedDate) {
        // deprecated / fallback.
        // https://developer.mozilla.org/en-US/docs/Web/API/File/lastModifiedDate
        modifiedDate = file.lastModifiedDate;
      }
      modifiedDate = modifiedDate.toString().substring(0, 21);
      formData.append(`lastModified_${index}`, modifiedDate);
      formData.append(`file_${index}`, file);
      formData.append(`replace_file_${index}_ids`, getFirstReplaceFileId(filesToUpload.value[index].name));
    }
    const headers = {
      'Content-Type': 'multipart/form-data',
      gsdevice: getDeviceIdFromCookie(),
    };
    apiPost('document/uploadfiles', formData, { headers })
      .then((response) => {
        if (response.resultCategory == 1) {
          filesToUpload.value = [];
          getDocuments(props.parentId);
        } else {
          getDocuments(props.parentId);
          filesToUpload.value = filesToUpload.value.filter((file) => response.payload.indexOf(file.name) >= 0);
        }
      })
      .finally(() => {
        uploading.value = false;
      });
  } catch (err) {
    window.messageBus.$emit('showMessage', { type: 'error', text: 'An error occured', subText: err, icon: true });
    uploading.value = false;
  }
}

watch(
  autoUpload,
  (newValue) => {
    if (newValue && filesToUpload.value.length > 0) {
      uploadFiles();
    }
  },
  {
    immediate: true,
  }
);
</script>
